"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AddExistingAdsModal = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const inputtext_1 = require("primereact/inputtext");
const CustomModal_1 = require("../../../components/custom-modal/CustomModal");
const AdMediaType_1 = require("../../../modules/advertising/enums/AdMediaType");
const useAdvertising_1 = require("../context/useAdvertising");
const useToast_1 = require("../../../hooks/toast/useToast");
const advertising_module_scss_1 = __importDefault(require("../advertising.module.scss"));
const AddExistingAdsModal = ({ visible, machineId, excludeIds, onHide, onConfirm, }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const { getAvailableAds } = (0, useAdvertising_1.useAdvertising)();
    const { showError } = (0, useToast_1.useToast)();
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [ads, setAds] = (0, react_1.useState)([]);
    const [search, setSearch] = (0, react_1.useState)('');
    const [selected, setSelected] = (0, react_1.useState)(new Set());
    const getAvailableAdsRef = (0, react_1.useRef)(getAvailableAds);
    const showErrorRef = (0, react_1.useRef)(showError);
    const tRef = (0, react_1.useRef)(t);
    (0, react_1.useEffect)(() => {
        getAvailableAdsRef.current = getAvailableAds;
        showErrorRef.current = showError;
        tRef.current = t;
    });
    (0, react_1.useEffect)(() => {
        if (!visible)
            return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const data = await getAvailableAdsRef.current(machineId);
                if (!cancelled)
                    setAds(data);
            }
            catch {
                if (!cancelled) {
                    showErrorRef.current(tRef.current('advertising.machineAds.addExisting.loadError'));
                    setAds([]);
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        };
        setSearch('');
        setSelected(new Set());
        load();
        return () => {
            cancelled = true;
        };
    }, [visible, machineId]);
    const visibleAds = (0, react_1.useMemo)(() => {
        const excluded = new Set(excludeIds);
        const term = search.trim().toLowerCase();
        return ads
            .filter((ad) => !excluded.has(ad.id))
            .filter((ad) => !term || ad.title.toLowerCase().includes(term));
    }, [ads, excludeIds, search]);
    const toggleAd = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    const handleConfirm = () => {
        if (selected.size === 0)
            return;
        const chosen = ads.filter((ad) => selected.has(ad.id));
        onConfirm(chosen);
    };
    const renderThumb = (ad) => {
        const isVideo = ad.mediaType === AdMediaType_1.AdMediaType.Video;
        if (ad.mediaUrl && isVideo) {
            return ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.pickerThumb, children: [(0, jsx_runtime_1.jsx)("video", { src: ad.mediaUrl, muted: true, playsInline: true, preload: "metadata", className: advertising_module_scss_1.default.pickerThumbMedia }), (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerThumbIcon, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-play-circle" }) })] }));
        }
        if (ad.mediaUrl && !isVideo) {
            return ((0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.pickerThumb, children: (0, jsx_runtime_1.jsx)("img", { src: ad.mediaUrl, alt: "", className: advertising_module_scss_1.default.pickerThumbMedia, onError: (e) => {
                        e.currentTarget.style.display = 'none';
                    } }) }));
        }
        return ((0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.pickerThumb, children: (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerThumbIcon, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: `pi ${isVideo ? 'pi-video' : 'pi-image'}` }) }) }));
    };
    return ((0, jsx_runtime_1.jsxs)(CustomModal_1.CustomModal, { visible: visible, onHide: onHide, title: t('advertising.machineAds.addExisting.title'), contentClassName: "!rounded-md", children: [(0, jsx_runtime_1.jsx)("p", { className: advertising_module_scss_1.default.pickerSubtitle, children: t('advertising.machineAds.addExisting.subtitle') }), (0, jsx_runtime_1.jsxs)("label", { className: advertising_module_scss_1.default.pickerSearch, children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-search", "aria-hidden": true }), (0, jsx_runtime_1.jsx)(inputtext_1.InputText, { value: search, onChange: (e) => setSearch(e.target.value), placeholder: t('advertising.machineAds.addExisting.searchPlaceholder') })] }), (0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.pickerList, children: loading ? ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.pickerEmpty, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerEmptyIcon, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-spin pi-spinner" }) }), (0, jsx_runtime_1.jsx)("p", { children: t('advertising.machineAds.addExisting.loading') })] })) : visibleAds.length === 0 ? ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.pickerEmpty, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerEmptyIcon, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-inbox" }) }), (0, jsx_runtime_1.jsx)("p", { children: t('advertising.machineAds.addExisting.empty') })] })) : (visibleAds.map((ad) => {
                    const isSelected = selected.has(ad.id);
                    return ((0, jsx_runtime_1.jsxs)("button", { type: "button", className: `${advertising_module_scss_1.default.pickerRow} ${isSelected ? advertising_module_scss_1.default.pickerRowSelected : ''}`, onClick: () => toggleAd(ad.id), "aria-pressed": isSelected, children: [(0, jsx_runtime_1.jsx)("span", { className: `${advertising_module_scss_1.default.pickerCheck} ${isSelected ? advertising_module_scss_1.default.pickerCheckOn : ''}`, "aria-hidden": true, children: isSelected && (0, jsx_runtime_1.jsx)("i", { className: "pi pi-check" }) }), renderThumb(ad), (0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.pickerInfo, children: (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerTitle, title: ad.title, children: ad.title }) })] }, ad.id));
                })) }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.pickerFooter, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerSelectedCount, children: t('advertising.machineAds.addExisting.selected', {
                            count: selected.size,
                        }) }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.pickerFooterActions, children: [(0, jsx_runtime_1.jsx)("button", { type: "button", className: advertising_module_scss_1.default.btnGhost, onClick: onHide, children: t('advertising.machineAds.addExisting.cancel') }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: advertising_module_scss_1.default.btnPrimary, onClick: handleConfirm, disabled: selected.size === 0, children: t('advertising.machineAds.addExisting.confirm', {
                                    count: selected.size,
                                }) })] })] })] }));
};
exports.AddExistingAdsModal = AddExistingAdsModal;
